import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, ChevronRight, RotateCcw } from 'lucide-react';
import {
    assistantChat,
    getAssistantContext,
    getAssistantSuggestedPrompts,
    getAssistantHistory,
    clearAssistantHistory,
    AssistantPageContext,
    ASSISTANT_STREAM_URL,
} from '../../services/api';
import './Assistant.css';

// ---------------------------------------------------------------------------
// Page context mapping
// ---------------------------------------------------------------------------

const getModuleContext = (pathname: string): { module: string; title: string } => {
    if (pathname.startsWith('/campaigns/create')) return { module: 'campaigns', title: 'Create Campaign' };
    if (pathname.startsWith('/campaigns/')) return { module: 'campaigns', title: 'Campaign Detail' };
    if (pathname === '/campaigns') return { module: 'campaigns', title: 'Campaigns' };
    if (pathname === '/seo') return { module: 'seo', title: 'SEO Tools' };
    if (pathname === '/analytics') return { module: 'analytics', title: 'Analytics' };
    if (pathname === '/multichannel') return { module: 'multichannel', title: 'Multi-Channel Content' };
    if (pathname === '/lead-capture') return { module: 'lead-capture', title: 'Lead Capture' };
    if (pathname === '/reviews') return { module: 'reviews', title: 'Reviews' };
    if (pathname === '/images') return { module: 'images', title: 'Images' };
    if (pathname === '/brand') return { module: 'brand', title: 'Brand Portal' };
    if (pathname === '/catalogue') return { module: 'content', title: 'Content Catalogue' };
    if (pathname.startsWith('/landing-pages/create')) return { module: 'landing_pages', title: 'Create Landing Page' };
    if (pathname === '/landing-pages') return { module: 'landing_pages', title: 'Landing Pages' };
    if (pathname.startsWith('/scheduling')) return { module: 'scheduling', title: 'Scheduling' };
    if (pathname === '/quizzes') return { module: 'quizzes', title: 'Quizzes' };
    if (pathname === '/scripts') return { module: 'scripts', title: 'Scripts' };
    if (pathname === '/websites-v2') return { module: 'websites_v2', title: 'Websites V2' };
    if (pathname.startsWith('/websites')) return { module: 'websites', title: 'Websites' };
    if (pathname.startsWith('/content')) return { module: 'content', title: 'Content Creator' };
    if (pathname === '/dashboard') return { module: 'dashboard', title: 'Dashboard' };
    if (pathname === '/leads') return { module: 'leads_crm', title: 'Leads / CRM' };
    if (pathname.startsWith('/teams')) return { module: 'teams', title: 'Teams' };
    if (pathname === '/profile') return { module: 'general', title: 'My Profile' };
    if (pathname.startsWith('/admin')) return { module: 'admin', title: 'Admin' };
    return { module: 'general', title: 'AiMe' };
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    navigate_to?: string;
    action?: string;
    isProgress?: boolean;
}

// ---------------------------------------------------------------------------
// AssistantWidget
// ---------------------------------------------------------------------------

const AssistantWidget: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [greeting, setGreeting] = useState<string>('');
    const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);
    const [showPrompts, setShowPrompts] = useState(true);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const prefetchedRef = useRef<{ messages: ChatMessage[]; greeting: string; prompts: string[] } | null>(null);
    const prefetchingRef = useRef(false);

    const pageCtx = getModuleContext(location.pathname);
    const pageContext: AssistantPageContext = useMemo(() => ({
        path: location.pathname,
        module: pageCtx.module,
        page_title: pageCtx.title,
    }), [location.pathname, pageCtx.module, pageCtx.title]);

    // Prefetch assistant state in background on mount — so opening is instant
    useEffect(() => {
        if (prefetchingRef.current || prefetchedRef.current) return;
        prefetchingRef.current = true;
        (async () => {
            try {
                const [historyData, ctxData, prompts] = await Promise.all([
                    getAssistantHistory(),
                    getAssistantContext(),
                    getAssistantSuggestedPrompts(pageContext),
                ]);
                const restored: ChatMessage[] = (historyData.messages || []).map(
                    (m: { role: string; content: string; created_at: string }, idx: number) => ({
                        id: `h-${idx}-${m.created_at || Date.now()}`,
                        role: m.role as 'user' | 'assistant',
                        content: m.content,
                    })
                );
                prefetchedRef.current = {
                    messages: restored,
                    greeting: ctxData.greeting || '',
                    prompts: prompts.slice(0, 4),
                };
            } catch {
                prefetchedRef.current = {
                    messages: [],
                    greeting: '',
                    prompts: [
                        "What can AiMe do for me?",
                        "Help me plan my marketing",
                        "Show me what's been created recently",
                        "What should I work on next?",
                    ],
                };
            }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll to bottom whenever messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isLoading]);

    // When the widget opens, use prefetched data or fetch fresh
    const handleOpen = useCallback(async () => {
        setIsOpen(true);
        setTimeout(() => inputRef.current?.focus(), 100);

        // Use prefetched data if available (instant open)
        if (prefetchedRef.current) {
            const cached = prefetchedRef.current;
            setGreeting(cached.greeting);
            setSuggestedPrompts(cached.prompts);
            if (cached.messages.length > 0) {
                setMessages(cached.messages);
                setShowPrompts(false);
            }
            prefetchedRef.current = null; // consume — next open fetches fresh
            return;
        }

        // Fallback: fetch fresh if prefetch hasn't completed
        try {
            const [historyData, ctxData, prompts] = await Promise.all([
                getAssistantHistory(),
                getAssistantContext(),
                getAssistantSuggestedPrompts(pageContext),
            ]);

            setGreeting(ctxData.greeting || '');
            setSuggestedPrompts(prompts.slice(0, 4));

            if (historyData.messages && historyData.messages.length > 0) {
                const restored: ChatMessage[] = historyData.messages.map(
                    (m: { role: string; content: string; created_at: string }, idx: number) => ({
                        id: `h-${idx}-${m.created_at || Date.now()}`,
                        role: m.role as 'user' | 'assistant',
                        content: m.content,
                    })
                );
                setMessages(restored);
                setShowPrompts(false);
            }
        } catch {
            setSuggestedPrompts([
                "What can AiMe do for me?",
                "Help me plan my marketing",
                "Show me what's been created recently",
                "What should I work on next?",
            ]);
        }
    }, [pageContext]); // eslint-disable-line react-hooks/exhaustive-deps

    // Refresh suggested prompts when page changes (only if widget is open and idle)
    useEffect(() => {
        if (isOpen && messages.length === 0) {
            getAssistantSuggestedPrompts(pageContext)
                .then(p => setSuggestedPrompts(p.slice(0, 4)))
                .catch(() => {});
        }
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    const sendMessage = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed || isLoading) return;

        const userMsg: ChatMessage = {
            id: `u-${Date.now()}`,
            role: 'user',
            content: trimmed,
        };
        setMessages(prev => [...prev, userMsg]);
        setInputValue('');
        setIsLoading(true);
        setShowPrompts(false);

        const progressMsgId = `progress-${Date.now()}`;

        try {
            // Add a progress placeholder message
            setMessages(prev => [...prev, {
                id: progressMsgId,
                role: 'assistant' as const,
                content: 'Connecting...',
                isProgress: true,
            }]);

            const token = localStorage.getItem('access_token');
            const response = await fetch(ASSISTANT_STREAM_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                    message: trimmed,
                    conversation_history: [],
                    page_context: pageContext,
                }),
            });

            if (!response.ok || !response.body) {
                throw new Error(`Stream request failed: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                // Parse SSE events from buffer — split on double-newline boundaries
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // keep incomplete line in buffer

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const json = line.slice(6).trim();
                    if (!json || json === '[DONE]') continue;

                    try {
                        const event = JSON.parse(json);

                        if (event.type === 'progress') {
                            // Update the progress message content in-place
                            setMessages(prev => prev.map(m =>
                                m.id === progressMsgId
                                    ? { ...m, content: event.message || event.step || 'Processing...' }
                                    : m
                            ));
                        } else if (event.type === 'tool_result') {
                            // Brief tool result — show as progress step
                            setMessages(prev => prev.map(m =>
                                m.id === progressMsgId
                                    ? { ...m, content: event.message || 'Executing action...' }
                                    : m
                            ));
                        } else if (event.type === 'response') {
                            // Final response — replace the progress message with real content
                            setMessages(prev => prev.map(m =>
                                m.id === progressMsgId
                                    ? {
                                        id: `a-${Date.now()}`,
                                        role: 'assistant' as const,
                                        content: event.response,
                                        navigate_to: event.navigate_to,
                                        action: event.action,
                                        isProgress: false,
                                    }
                                    : m
                            ));
                            if (event.suggested_prompts && event.suggested_prompts.length > 0) {
                                setSuggestedPrompts(event.suggested_prompts.slice(0, 3));
                                setShowPrompts(true);
                            }
                        } else if (event.type === 'error') {
                            setMessages(prev => prev.map(m =>
                                m.id === progressMsgId
                                    ? { ...m, content: event.message || 'Assistant encountered an error.', isProgress: false }
                                    : m
                            ));
                            setIsLoading(false);
                        } else if (event.type === 'done') {
                            setIsLoading(false);
                        }
                    } catch {
                        // Ignore malformed JSON lines
                    }
                }
            }

            // Stream ended — ensure loading is off
            setIsLoading(false);

        } catch {
            // Streaming failed — fall back to the synchronous endpoint
            try {
                // Remove the progress message before falling back
                setMessages(prev => prev.filter(m => m.id !== progressMsgId));

                const result = await assistantChat({
                    message: trimmed,
                    conversation_history: [],
                    page_context: pageContext,
                });

                const assistantMsg: ChatMessage = {
                    id: `a-${Date.now()}`,
                    role: 'assistant',
                    content: result.response,
                    navigate_to: result.navigate_to,
                    action: result.action,
                };
                setMessages(prev => [...prev, assistantMsg]);
                if (result.suggested_prompts && result.suggested_prompts.length > 0) {
                    setSuggestedPrompts(result.suggested_prompts.slice(0, 3));
                    setShowPrompts(true);
                }
            } catch {
                // Both streaming and fallback failed
                setMessages(prev => {
                    const withoutProgress = prev.filter(m => m.id !== progressMsgId);
                    return [...withoutProgress, {
                        id: `e-${Date.now()}`,
                        role: 'assistant' as const,
                        content: 'Sorry, I had trouble reaching the AI. Please try again in a moment.',
                    }];
                });
            } finally {
                setIsLoading(false);
            }
        }
    }, [isLoading, pageContext]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage(inputValue);
        }
    };

    const handlePromptClick = (prompt: string) => {
        sendMessage(prompt);
    };

    const handleClear = useCallback(async () => {
        try {
            await clearAssistantHistory();
        } catch {
            // Best-effort — clear local state regardless
        }
        setMessages([]);
        setShowPrompts(true);
        // Refresh suggested prompts for current page
        getAssistantSuggestedPrompts(pageContext)
            .then(p => setSuggestedPrompts(p.slice(0, 4)))
            .catch(() => {});
    }, [pageContext]);

    const handleNavigate = (path: string) => {
        navigate(path);
        setIsOpen(false);
    };

    // ---------------------------------------------------------------------------
    // Collapsed state — just the FAB button
    // ---------------------------------------------------------------------------

    if (!isOpen) {
        return (
            <button
                className="assistant-fab"
                onClick={handleOpen}
                title="AiMe Assistant"
                aria-label="Open AiMe Assistant"
            >
                <Sparkles size={22} />
            </button>
        );
    }

    // ---------------------------------------------------------------------------
    // Expanded state — the panel
    // ---------------------------------------------------------------------------

    const isEmpty = messages.length === 0;

    return (
        <div className="assistant-panel" role="dialog" aria-label="AiMe Assistant">
            {/* Header */}
            <div className="assistant-header">
                <Sparkles size={16} color="var(--primary)" />
                <span className="assistant-header-title">AiMe Assistant</span>
                <span className="assistant-page-chip" title={pageCtx.title}>
                    {pageCtx.title}
                </span>
                <button
                    className="assistant-close-btn"
                    onClick={handleClear}
                    aria-label="Clear conversation"
                    title="Clear conversation"
                    style={{ marginRight: 2 }}
                >
                    <RotateCcw size={14} />
                </button>
                <button
                    className="assistant-close-btn"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close assistant"
                >
                    <X size={16} />
                </button>
            </div>

            {/* Messages */}
            <div className="assistant-messages">
                {/* Greeting / empty state */}
                {isEmpty && (
                    <div className="assistant-greeting">
                        <div className="assistant-greeting-icon">
                            <Sparkles size={28} />
                        </div>
                        {greeting ? (
                            <p className="assistant-greeting-text">{greeting}</p>
                        ) : (
                            <p className="assistant-greeting-text">
                                Hi! I'm your AiMe Assistant. Ask me anything about your marketing or how to use AiMe.
                            </p>
                        )}
                    </div>
                )}

                {/* Message bubbles */}
                {messages.map(msg => (
                    <div key={msg.id} className={`assistant-message ${msg.role}${msg.isProgress ? ' progress' : ''}`}>
                        <div className="assistant-message-bubble">
                            {msg.content}
                        </div>
                        {msg.role === 'assistant' && msg.navigate_to && (
                            <button
                                className="assistant-navigate-chip"
                                onClick={() => handleNavigate(msg.navigate_to!)}
                            >
                                <ChevronRight size={12} />
                                Go to {msg.navigate_to}
                            </button>
                        )}
                    </div>
                ))}

                {/* Typing indicator — hidden when a progress message is visible */}
                {isLoading && !messages.some(m => m.isProgress) && (
                    <div className="assistant-typing">
                        <div className="assistant-typing-dot" />
                        <div className="assistant-typing-dot" />
                        <div className="assistant-typing-dot" />
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* Suggested prompts */}
            {showPrompts && suggestedPrompts.length > 0 && (
                <div className="assistant-prompt-chips">
                    {suggestedPrompts.map((prompt, i) => (
                        <button
                            key={i}
                            className="assistant-prompt-chip"
                            onClick={() => handlePromptClick(prompt)}
                            disabled={isLoading}
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            <div className="assistant-input-row">
                <input
                    ref={inputRef}
                    className="assistant-input"
                    type="text"
                    placeholder="Ask me anything..."
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                    maxLength={1000}
                    aria-label="Message to AiMe assistant"
                />
                <button
                    className="assistant-send-btn"
                    onClick={() => sendMessage(inputValue)}
                    disabled={isLoading || !inputValue.trim()}
                    aria-label="Send message"
                >
                    <Send size={15} />
                </button>
            </div>
        </div>
    );
};

export default AssistantWidget;
